import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { CHAT_URL } from "@/lib/api";

const SESSION_KEY = "strive_chat_session";
const HISTORY_KEY = "strive_chat_history";

const GREETING = {
  role: "assistant",
  content:
    "Hey · I'm the Strive concierge. Tell me the vehicle you're after (make, model, year, budget) and I'll get to work. Or ask me anything about how we source cars.",
};

const SUGGESTIONS = [
  "Do you have a 2022 Ford Bronco?",
  "Find me a low-mile Porsche 911",
  "I want to trade my SUV",
];

export default function ConciergeChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) return JSON.parse(raw);
    } catch {}
    return [GREETING];
  });
  const scroller = useRef(null);
  const inputRef = useRef(null);

  const sessionId = useMemo(() => {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-40)));
    } catch {}
    if (scroller.current) {
      scroller.current.scrollTop = scroller.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  // ESC to close for keyboard users
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const send = async (rawText) => {
    const text = (rawText ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(CHAT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("network");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split("\n\n");
        buf = frames.pop() || "";
        for (const frame of frames) {
          const line = frame.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + parsed.delta,
                };
                return copy;
              });
            } else if (parsed.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content:
                    "Sorry · I hit an issue reaching our system. Call us directly at 561-502-6769 and we'll help right away.",
                };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content:
            "I couldn't reach our system just now. Call us at 561-502-6769 or use the Find My Car form and we'll get right on it.",
        };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        data-testid="concierge-fab"
        onClick={() => setOpen((o) => !o)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.8, duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed right-4 lg:right-6 bottom-24 lg:bottom-6 z-40 group"
        aria-label="Open concierge chat"
      >
        <span className="absolute inset-0 rounded-full bg-[#E31E24] opacity-40 group-hover:opacity-70 blur-xl transition-opacity" />
        <span className="relative flex items-center justify-center w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-[#E31E24] text-white border border-white/20 shadow-2xl">
          {open ? <X size={22} /> : <MessageCircle size={22} />}
        </span>
        {!open && (
          <span className="hidden lg:flex absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap items-center bg-[#141414] text-white px-3 py-1.5 border border-[#262626] font-mono2 text-[11px] uppercase tracking-[0.22em]">
            Ask the Concierge
          </span>
        )}
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.aside
            data-testid="concierge-panel"
            role="dialog"
            aria-modal="false"
            aria-labelledby="concierge-title"
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.7, 0, 0.2, 1] }}
            className="fixed z-50 right-4 lg:right-6 bottom-44 lg:bottom-28 w-[calc(100vw-2rem)] sm:w-[400px] lg:w-[420px] max-h-[70vh] flex flex-col border border-[#E31E24]/40 bg-[#0A0A0A]/95 backdrop-blur-xl shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-[#E31E24] animate-pulse" aria-hidden="true" />
                <div>
                  <div id="concierge-title" className="font-anton uppercase tracking-[0.14em] text-white text-sm">
                    Strive Concierge
                  </div>
                  <div className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-white/60">
                    Online · Replies in seconds
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="concierge-close"
                className="text-white/60 hover:text-white"
                aria-label="Close chat"
              >
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scroller}
              data-testid="concierge-messages"
              role="log"
              aria-live="polite"
              aria-label="Chat conversation"
              className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
            >
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-[14px] leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#E31E24] text-white"
                        : "bg-[#141414] text-white/90 border border-white/10"
                    }`}
                  >
                    {m.content || (
                      <span className="inline-flex items-center gap-2 text-white/60">
                        <Loader2 size={14} className="animate-spin" /> thinking…
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      data-testid={`concierge-suggestion-${i}`}
                      className="border border-white/20 hover:border-[#E31E24] hover:text-[#E31E24] px-3 py-1.5 text-[12px] font-mono2 uppercase tracking-[0.2em] text-white/70 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-white/10 px-3 py-3 flex items-center gap-2"
            >
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="concierge-input"
                aria-label="Ask the Strive concierge a question"
                placeholder="Do you have a 2022 Bronco?"
                className="flex-1 bg-transparent outline-none text-white placeholder-white/50 px-3 py-2 text-[14px]"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="concierge-send"
                className="inline-flex items-center justify-center w-10 h-10 bg-[#E31E24] hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Send message"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </form>
            <div className="px-5 pb-3 font-mono2 text-[9px] uppercase tracking-[0.28em] text-white/35">
              Powered by Claude · Answers based on Strive's process
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
